import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  AUTH_QUERY,
  GET_MY_CONTACTS_QUERY,
  GET_MY_SITES_QUERY,
  GET_OBJECT_GROUP_QUERY,
  GET_OBJECT_GROUP_OBJECTS_QUERY,
  SET_OBJECT_GROUP_COLLABORATORS_MUTATION,
  UPDATE_OBJECT_GROUP_MUTATION,
} from '../../gql';
import {
  Button,
  FieldLabel,
  FormField,
  IconButton,
  SurfaceAutocomplete,
  SurfaceSelect,
  SurfaceTextField,
  TabBar,
  TabBarItem,
} from '../../components/design-system';
import { ObjectsTable } from './ObjectsTable';
import { ContentSafetyActions } from '../../components/ContentSafetyActions';

const paperStyles = { p: 3 };
const contactInputStyles = { width: 320 };
const levelSelectStyles = { minWidth: 96 };
const levels = [0, 1, 2] as const;

interface Contact {
  id: string;
  email: string;
  displayName?: string | null;
  responderUser: string;
}

interface Collaborator {
  id: string;
  userId: string;
  level: number;
  email: string;
  displayName?: string | null;
}

interface StagedCollaborator {
  userId: string;
  email: string;
  displayName?: string | null;
  level: number;
}

type FormValues = {
  name: string;
  description: string;
};

type ObjectGroupTab = 'overview' | 'objects';

interface ObjectGroupDetailProps {
  activeTab: ObjectGroupTab;
}

const emptyDefaults: FormValues = { name: '', description: '' };

const getContactOptionLabel = (contact: Contact) =>
  `${contact.email}${contact.displayName ? ` (${contact.displayName})` : ''}`;

const isContactOptionEqual = (option: Contact, value: Contact) =>
  option.id === value.id;

const ObjectGroupDetail = ({ activeTab }: ObjectGroupDetailProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { groupId } = useParams();

  const { data: authData } = useQuery(AUTH_QUERY);
  const viewerId: string | undefined = authData?.viewer?.user?.id;

  const { data, loading, refetch } = useQuery(GET_OBJECT_GROUP_QUERY, {
    variables: { id: groupId },
    fetchPolicy: 'cache-and-network',
    skip: !groupId,
  });

  const group = data?.getObjectGroup;
  const isOwner = !!viewerId && !!group && group.createdBy === viewerId;

  const { data: contactsData } = useQuery(GET_MY_CONTACTS_QUERY, {
    fetchPolicy: 'cache-first',
    skip: activeTab !== 'overview',
  });
  const contacts: Contact[] = contactsData?.getMyContacts?.contacts ?? [];

  const { data: objectsData, loading: objectsLoading } = useQuery(
    GET_OBJECT_GROUP_OBJECTS_QUERY,
    {
      variables: { groupId },
      fetchPolicy: 'cache-and-network',
      skip: activeTab !== 'objects' || !groupId,
    }
  );
  const { data: sitesData, loading: sitesLoading } = useQuery(
    GET_MY_SITES_QUERY,
    {
      fetchPolicy: 'cache-first',
      skip: activeTab !== 'objects',
    }
  );
  const objects = objectsData?.getMyObjects ?? [];
  const sites = sitesData?.getMySites ?? [];
  const objectGroups = useMemo(() => (group ? [group] : []), [group]);

  const defaultValues: FormValues = useMemo(() => {
    if (!group) {
      return emptyDefaults;
    }

    return {
      name: group.name ?? '',
      description: group.description ?? '',
    };
  }, [group]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [staged, setStaged] = useState<StagedCollaborator[]>([]);
  useEffect(() => {
    const initial: StagedCollaborator[] = (group?.collaborators ?? []).map(
      (c: Collaborator) => ({
        userId: c.userId,
        email: c.email,
        displayName: c.displayName,
        level: c.level,
      })
    );
    setStaged(initial);
  }, [group]);

  const stagedUserIds = useMemo(() => new Set(staged.map((s) => s.userId)), [
    staged,
  ]);
  const contactOptions = contacts.filter(
    (c) => !stagedUserIds.has(c.responderUser)
  );
  const contactTextFieldProps = useMemo(
    () => ({
      placeholder: isOwner
        ? intl.formatMessage({
            id: 'objects.groups.addContact',
            defaultMessage: 'Add contact by email',
          })
        : intl.formatMessage({
            id: 'objects.groups.ownerOnly',
            defaultMessage: 'Only the owner can change access',
          }),
    }),
    [intl, isOwner]
  );

  const [pickContact, setPickContact] = useState<Contact | null>(null);
  const handleAddContact = (contact: Contact | null) => {
    if (!contact) {
      return;
    }

    setStaged((prev) => [
      ...prev,
      {
        userId: contact.responderUser,
        email: contact.email,
        displayName: contact.displayName,
        level: 1,
      },
    ]);
    setPickContact(null);
  };
  const handleChangeLevel = (userId: string, level: number) => {
    setStaged((prev) =>
      prev.map((s) => (s.userId === userId ? { ...s, level } : s))
    );
  };
  const handleRemove = (userId: string) => {
    setStaged((prev) => prev.filter((s) => s.userId !== userId));
  };

  const [updateGroup, { error: updateError }] = useMutation(
    UPDATE_OBJECT_GROUP_MUTATION
  );
  const [setCollaborators, { error: setError }] = useMutation(
    SET_OBJECT_GROUP_COLLABORATORS_MUTATION
  );

  const tabs = useMemo<TabBarItem[]>(
    () => [
      {
        label: intl.formatMessage({
          id: 'objects.groups.tabs.overview',
          defaultMessage: 'Overview',
        }),
        value: 'overview',
      },
      {
        label: intl.formatMessage({
          id: 'objects.groups.tabs.objects',
          defaultMessage: 'Objects',
        }),
        value: 'objects',
      },
    ],
    [intl]
  );
  const handleTabChange = useCallback(
    (value: string) => {
      if (!groupId) {
        return;
      }

      const path =
        value === 'objects'
          ? `/o/groups/${groupId}/objects`
          : `/o/groups/${groupId}`;
      navigate(path);
    },
    [groupId, navigate]
  );

  const submit = async (values: FormValues) => {
    if (!groupId || !isOwner) {
      return;
    }

    await updateGroup({
      variables: {
        id: groupId,
        group: {
          name: values.name || null,
          description: values.description || null,
        },
      },
    });
    await setCollaborators({
      variables: {
        id: groupId,
        collaborators: staged.map((s) => ({
          userId: s.userId,
          level: s.level,
        })),
      },
    });
    await refetch();
  };

  const submitForm = handleSubmit(submit);
  const safetyActions = (
    <Stack alignItems="flex-end">
      <ContentSafetyActions
        enabled={Boolean(group?.shared)}
        entityId={groupId}
        entityType="OBJECT_GROUP"
        ownerUserId={group?.createdBy}
      />
    </Stack>
  );

  if (!group) {
    return (
      <Stack spacing={2}>
        {safetyActions}
        <Paper sx={paperStyles}>
          <Typography variant="body2">
            {loading ? (
              <FormattedMessage
                id="objects.groups.loading"
                defaultMessage="Loading group…"
              />
            ) : (
              <FormattedMessage
                id="objects.groups.notFound"
                defaultMessage="Group not found."
              />
            )}
          </Typography>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {safetyActions}
      <TabBar
        aria-label={intl.formatMessage({
          id: 'objects.groups.tabs.label',
          defaultMessage: 'Object group sections',
        })}
        items={tabs}
        onChange={handleTabChange}
        value={activeTab}
      />

      {activeTab === 'objects' ? (
        <ObjectsTable
          groups={objectGroups}
          loading={objectsLoading || sitesLoading}
          rows={objects}
          sites={sites}
        />
      ) : (
        <Paper sx={paperStyles}>
          <Box component="form" onSubmit={submitForm}>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Typography variant="h6">
                  <FormattedMessage
                    id="objects.groups.settings"
                    defaultMessage="Group settings"
                  />
                </Typography>

                <FormField>
                  <FieldLabel htmlFor="group-name">
                    <FormattedMessage
                      id="objects.groups.table.name"
                      defaultMessage="Name"
                    />
                  </FieldLabel>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        id="group-name"
                        surface="sunken"
                        disabled={!isOwner}
                        {...field}
                      />
                    )}
                  />
                </FormField>

                <FormField>
                  <FieldLabel htmlFor="group-description">
                    <FormattedMessage
                      id="objects.groups.table.description"
                      defaultMessage="Description"
                    />
                  </FieldLabel>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <SurfaceTextField
                        id="group-description"
                        multiline
                        minRows={2}
                        maxRows={5}
                        surface="sunken"
                        disabled={!isOwner}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="h6">
                  <FormattedMessage
                    id="objects.groups.access"
                    defaultMessage="Access"
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage
                    id="objects.groups.accessHelp"
                    defaultMessage="Level 0 is read-only, level 1 can add objects, and level 2 can edit."
                  />
                </Typography>

                <SurfaceAutocomplete
                  disablePortal
                  id="group-add-contact"
                  options={contactOptions}
                  value={pickContact}
                  onChange={(_, next) => handleAddContact(next)}
                  disabled={!isOwner || contactOptions.length === 0}
                  isOptionEqualToValue={isContactOptionEqual}
                  getOptionLabel={getContactOptionLabel}
                  surface="sunken"
                  textFieldProps={contactTextFieldProps}
                  sx={contactInputStyles}
                />

                {staged.length > 0 ? (
                  <Stack spacing={0.75}>
                    {staged.map((s) => (
                      <Stack
                        key={s.userId}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Chip
                          label={
                            s.displayName
                              ? `${s.email} (${s.displayName})`
                              : s.email
                          }
                          size="small"
                        />
                        <SurfaceSelect
                          value={s.level}
                          onChange={(e) =>
                            handleChangeLevel(s.userId, Number(e.target.value))
                          }
                          disabled={!isOwner}
                          surface="sunken"
                          sx={levelSelectStyles}
                        >
                          {levels.map((l) => (
                            <MenuItem key={l} value={l}>
                              {intl.formatMessage(
                                {
                                  id: 'objects.groups.level',
                                  defaultMessage: 'Level {level}',
                                },
                                { level: l }
                              )}
                            </MenuItem>
                          ))}
                        </SurfaceSelect>
                        <IconButton
                          density="compact"
                          onClick={() => handleRemove(s.userId)}
                          disabled={!isOwner}
                          tone="danger"
                          variant="ghost"
                          aria-label={intl.formatMessage({
                            id: 'objects.groups.removeCollaborator',
                            defaultMessage: 'Remove collaborator',
                          })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage
                      id="objects.groups.noCollaborators"
                      defaultMessage="No collaborators yet."
                    />
                  </Typography>
                )}
              </Stack>

              {(updateError || setError) && (
                <Alert severity="error" variant="outlined">
                  {updateError?.message || setError?.message}
                </Alert>
              )}

              {isOwner && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button type="submit" disabled={isSubmitting}>
                    <FormattedMessage id="general.save" defaultMessage="Save" />
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        </Paper>
      )}
    </Stack>
  );
};

export { ObjectGroupDetail };
